import type {
  CustomSeriesOption,
  CustomSeriesRenderItemAPI,
  CustomSeriesRenderItemParams,
  CustomSeriesRenderItemReturn,
  EChartsOption,
} from 'echarts';
import { format as echartsFormat } from 'echarts/core';

import {
  FONT_SIZE,
  FormatMetric,
  toBaseOption,
  toTooltipHtml,
  toTooltipStyle,
} from './shared';

// A row's text is sized to the room the row has, between the model's least
// and most (see BulletModel). Of the row's height:
const FONT_TO_ROW_HEIGHT = 0.36;
// Of the chart's width, so a short, wide chart's text doesn't swamp it
const FONT_TO_WIDTH = 0.04;
// The labels' share of the width, past which they're cut short
const LABEL_WIDTH_SHARE = 0.28;

// Of the row's height: the bands' track, and the value's bar on it
const TRACK_HEIGHT = 0.55;
const VALUE_HEIGHT = 0.22;
const BAND_OPACITY = 0.3;
const TARGET_WIDTH = 3;
// Of the row's text: a target's label above its marker, and how far a value
// is over or under its target beneath it
const SMALL_TEXT_SHARE = 0.45;
const MIN_SMALL_FONT_SIZE = 9;
// Of a value's text: its line, when its difference is beneath it
const LINE_HEIGHT = 1.15;

const STATUS_LABELS: Record<BulletCharts.Status, string> = {
  good: 'Good',
  ok: 'OK',
  poor: 'Poor',
  none: '',
};

export interface BulletGeometry {
  bands: { from: number; to: number; status: BulletCharts.Status }[];
  // From the baseline (0, or the domain's start when that's above it)
  value?: { from: number; to: number };
  target?: number;
}

// Where a row's parts sit along its track, from 0 (its domain's start) to 1
// (its end): each row is drawn on its own scale
export const toBulletGeometry = (row: BulletCharts.Row): BulletGeometry => {
  const [start, end] = row.domain;
  const toFraction = (n: number) =>
    Math.min(Math.max((n - start) / (end - start), 0), 1);
  const baseline = toFraction(Math.max(start, 0));
  return {
    bands: row.bands.map((band) => ({
      from: toFraction(band.from),
      to: toFraction(band.to),
      status: band.status,
    })),
    value:
      row.value === null
        ? undefined
        : {
            from: Math.min(baseline, toFraction(row.value)),
            to: Math.max(baseline, toFraction(row.value)),
          },
    target: row.target === undefined ? undefined : toFraction(row.target),
  };
};

// The text's size: as big as the rows' room allows, between the model's
// least and most (a wallboard's are far more)
export const toBulletFontSize = ({
  model,
  size,
}: {
  model: StudioCharts.BulletModel;
  size?: { width: number; height: number };
}): number => {
  if (!size || model.rows.length === 0) {
    return Math.max(FONT_SIZE, model.minFontSize);
  }
  const fitted = Math.min(
    (size.height / model.rows.length) * FONT_TO_ROW_HEIGHT,
    size.width * FONT_TO_WIDTH,
    model.maxFontSize,
  );
  return Math.round(Math.max(fitted, model.minFontSize));
};

// The rows' labels' size: the text's, or smaller so the widest label fits
// its share of the width, never under the model's least (past which it's cut
// short). One size for every label, so they line up.
export const toBulletLabelFontSize = ({
  labels,
  fontSize,
  minFontSize,
  room,
  fontFamily,
}: {
  labels: string[];
  fontSize: number;
  minFontSize: number;
  // In pixels
  room?: number;
  fontFamily: string;
}): number => {
  if (room === undefined || room <= 0) {
    return fontSize;
  }
  const font = `${fontSize}px ${fontFamily}`;
  const widest = Math.max(
    0,
    ...labels.map((label) => echartsFormat.getTextRect(label, font).width),
  );
  return widest <= room
    ? fontSize
    : Math.max(minFontSize, Math.floor((fontSize * room) / widest));
};

// How far a value is from its target, beside it: e.g. "148 over"
export const toBulletDifference = ({
  row,
  format,
}: {
  row: BulletCharts.Row;
  format: (value: number) => string;
}): string | undefined => {
  if (row.value === null || row.target === undefined) {
    return undefined;
  }
  const difference = row.value - row.target;
  if (difference === 0) {
    return 'On target';
  }
  return `${format(Math.abs(difference))} ${difference > 0 ? 'over' : 'under'}`;
};

// Rich text can't hold its own markup
const toRichText = (text: string) => text.replace(/[{}|]/g, '');

// A bullet chart: per row, its label on the left, its value on the right,
// and between them its bands, its value's bar and its target's marker. One
// custom series draws them, each row on its own scale (see
// toBulletGeometry), against a category axis of the rows and a hidden 0 to 1
// value axis.
const toBulletOption = ({
  model,
  theme,
  formatMetric,
  size,
}: {
  model: StudioCharts.BulletModel;
  theme: StudioCharts.Theme;
  formatMetric: FormatMetric;
  size?: { width: number; height: number };
}): EChartsOption => {
  const { rows } = model;
  const display = model.display;
  const fontSize = toBulletFontSize({ model, size });
  const smallFontSize = Math.max(
    Math.round(fontSize * SMALL_TEXT_SHARE),
    MIN_SMALL_FONT_SIZE,
  );
  const labelWidth = size ? size.width * LABEL_WIDTH_SHARE : undefined;
  const labelFontSize = toBulletLabelFontSize({
    labels: rows.map((row) => row.label),
    fontSize,
    minFontSize: model.minFontSize,
    room: labelWidth,
    fontFamily: theme.fontFamily,
  });
  const format = (row: BulletCharts.Row, value: number) =>
    formatMetric({ metricId: row.metricId, value });
  const isDeselected = (row: BulletCharts.Row) =>
    !!model.selectedKeys &&
    row.term !== undefined &&
    !model.selectedKeys.includes(row.term);
  const statusColors: Record<BulletCharts.Status, string> = {
    good: theme.success,
    ok: theme.warning,
    poor: theme.danger,
    none: theme.textMuted,
  };

  const renderItem = (
    params: CustomSeriesRenderItemParams,
    api: CustomSeriesRenderItemAPI,
  ): CustomSeriesRenderItemReturn => {
    const row = rows[params.dataIndex];
    if (!row) {
      return { type: 'group', children: [] };
    }
    const geometry = toBulletGeometry(row);
    const [left, middle] = api.coord([0, params.dataIndex]);
    const [right] = api.coord([1, params.dataIndex]);
    // A category's height: what one step along the row axis spans
    const rowHeight = api.size
      ? (api.size([0, 1]) as number[])[1]
      : Math.abs(api.coord([0, params.dataIndex + 1])[1] - middle);
    const width = right - left;
    const trackHeight = rowHeight * TRACK_HEIGHT;
    const trackTop = middle - trackHeight / 2;
    const valueHeight = rowHeight * VALUE_HEIGHT;
    const isFaded = isDeselected(row);
    const at = (fraction: number) => left + fraction * width;
    const targetTop = trackTop - trackHeight * 0.1;
    // Without its bands, a row's track is plain: its bar alone shows its
    // status
    const bands = display.showBands
      ? geometry.bands
      : [{ from: 0, to: 1, status: 'none' as const }];

    return {
      type: 'group',
      children: [
        ...bands.map((band) => ({
          type: 'rect' as const,
          shape: {
            x: at(band.from),
            y: trackTop,
            width: at(band.to) - at(band.from),
            height: trackHeight,
          },
          style: {
            fill:
              band.status === 'none' ? theme.track : statusColors[band.status],
            opacity:
              band.status === 'none'
                ? 1
                : isFaded
                  ? BAND_OPACITY / 2
                  : BAND_OPACITY,
          },
        })),
        ...(geometry.value
          ? [
              {
                type: 'rect' as const,
                shape: {
                  x: at(geometry.value.from),
                  y: middle - valueHeight / 2,
                  width: at(geometry.value.to) - at(geometry.value.from),
                  height: valueHeight,
                },
                style: {
                  fill: isFaded ? theme.deselected : statusColors[row.status],
                },
              },
            ]
          : []),
        ...(geometry.target === undefined
          ? []
          : [
              {
                type: 'line' as const,
                shape: {
                  x1: at(geometry.target),
                  y1: targetTop,
                  x2: at(geometry.target),
                  y2: trackTop + trackHeight * 1.1,
                },
                style: {
                  stroke: isFaded ? theme.deselected : theme.textPrimary,
                  lineWidth: TARGET_WIDTH,
                },
              },
            ]),
        ...(geometry.target === undefined ||
        row.target === undefined ||
        !display.showTargetLabels
          ? []
          : [
              {
                type: 'text' as const,
                style: {
                  text: format(row, row.target),
                  x: at(geometry.target),
                  y: targetTop - 2,
                  align: 'center' as const,
                  verticalAlign: 'bottom' as const,
                  fill: isFaded ? theme.deselected : theme.textPrimary,
                  fontSize: smallFontSize,
                  fontWeight: 600,
                  fontFamily: theme.fontFamily,
                },
              },
            ]),
      ],
    };
  };

  const series: CustomSeriesOption = {
    type: 'custom',
    id: model.seriesId,
    name: rows[0] ? rows[0].label : '',
    renderItem,
    // A row each: its place on the category axis
    data: rows.map((_row, index) => [0, index]),
    encode: { x: 0, y: 1 },
  };

  const axisLabel = {
    color: theme.textPrimary,
    fontSize,
  };
  const valueOf = (row: BulletCharts.Row) =>
    row.value === null ? '-' : format(row, row.value);
  // Each value in its status's colour, and how far it's over or under its
  // target beneath it, as they're switched on. Line heights are set, so the
  // two lines stack the same whatever the font's measured as.
  const isValueStyled = !!display.showStatusColours || !!display.showDifference;
  const valueColors: Record<BulletCharts.Status, string> = {
    ...statusColors,
    ok: theme.warningText,
    none: theme.textPrimary,
  };
  const valueText = {
    fontSize,
    fontWeight: 600,
    lineHeight: Math.round(fontSize * LINE_HEIGHT),
  };
  const styledValueLabel = {
    formatter: (_value: string, index: number) => {
      const row = rows[index];
      if (!row) {
        return '';
      }
      const style = isDeselected(row)
        ? 'faded'
        : display.showStatusColours
          ? row.status
          : 'none';
      const difference = display.showDifference
        ? toBulletDifference({ row, format: (value) => format(row, value) })
        : undefined;
      return [
        `{${style}|${toRichText(valueOf(row))}}`,
        ...(difference ? [`{difference|${toRichText(difference)}}`] : []),
      ].join('\n');
    },
    rich: {
      ...Object.fromEntries(
        (Object.keys(statusColors) as BulletCharts.Status[]).map((status) => [
          status,
          { ...valueText, color: valueColors[status] },
        ]),
      ),
      faded: { ...valueText, color: theme.deselected },
      difference: {
        color: theme.textMuted,
        fontSize: smallFontSize,
        fontWeight: 'normal' as const,
        lineHeight: Math.round(smallFontSize * LINE_HEIGHT),
      },
    },
  };
  return {
    ...toBaseOption(theme),
    grid: {
      left: 8,
      right: 8,
      top: 4,
      bottom: 4,
      outerBoundsMode: 'same',
      outerBoundsContain: 'axisLabel',
    },
    xAxis: { type: 'value', min: 0, max: 1, show: false },
    yAxis: [
      {
        type: 'category',
        inverse: true,
        data: rows.map((row) => row.label),
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          ...axisLabel,
          fontSize: labelFontSize,
          ...(labelWidth ? { width: labelWidth, overflow: 'truncate' } : {}),
        },
      },
      {
        type: 'category',
        inverse: true,
        position: 'right',
        data: rows.map(valueOf),
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          ...axisLabel,
          fontWeight: 600,
          ...(isValueStyled ? styledValueLabel : {}),
        },
      },
    ],
    tooltip: {
      ...toTooltipStyle(theme),
      trigger: 'item',
      formatter: (params) => {
        const item = Array.isArray(params) ? params[0] : params;
        const row =
          item && typeof item.dataIndex === 'number'
            ? rows[item.dataIndex]
            : undefined;
        if (!row) {
          return '';
        }
        const pctOfTarget =
          row.value !== null && row.target !== undefined && row.target !== 0
            ? Math.round((row.value / row.target) * 100)
            : undefined;
        return toTooltipHtml({
          title: row.label,
          rows: [
            {
              name: 'Value',
              value: row.value === null ? 'No data' : format(row, row.value),
              note: STATUS_LABELS[row.status] || undefined,
            },
            ...(row.target === undefined
              ? []
              : [
                  {
                    name: 'Target',
                    value: format(row, row.target),
                    note: [
                      row.targetLabel,
                      pctOfTarget === undefined
                        ? undefined
                        : `${pctOfTarget}% of target`,
                    ]
                      .filter(Boolean)
                      .join(' · '),
                  },
                ]),
            ...row.bands
              .filter((band) => band.status !== 'none')
              .map((band) => ({
                name: STATUS_LABELS[band.status],
                value: `${format(row, band.from)} to ${format(row, band.to)}`,
              })),
            ...(row.lowerIsBetter
              ? [{ name: 'Lower is better', value: 'Yes' }]
              : []),
            ...(row.targetWarning
              ? [{ name: 'Target', value: row.targetWarning }]
              : []),
          ],
        });
      },
    },
    series: [series],
  };
};

export default toBulletOption;
